"use client";

import { useRouter } from "next/navigation";
import React, { ReactElement, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import useGtmPageView from "@/app/common-hooks/gtm/use-gtm-page-view";
import useCustomPageTracking from "@/app/common-hooks/page-tracking/use-custom-page-tracking";
import HelpOverlayButton from "@/app/shared-components/help-overlay-button/help-overlay-button-wizard";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import Logo from "@/components/molecules/logo-component/logo";
import { FooterRenewal } from "@/components/molecules/renewals/footer/renewal-footer";
import RenewalContactPreferences from "@/components/organisms/renewals/review/contact-preferences/renewal-contact-preferences";
import UnSavedChangesWarningMessage from "@/components/organisms/un-saved-changes-warning-message/un-saved-changes-warning-message";
import withQuoteData from "@/components/with-quote-data";
import { RenewalReview } from "@/config/app-constant";
import useSaveRenewalReviewSubmit from "@/hooks/api/review-api/use-save-renewal-review-submit";
import useGtmViewQuoteData from "@/hooks/gtm/use-gtm-view-quote";
import useMediaQuery from "@/hooks/use-media-query/use-media-query-enhanced";
import { useLoading } from "@/providers/loading-provider";
import { useRenewalReviewStateContext } from "@/providers/renewal-review-state-provider";
import { RenewalContactPreferencesContentType } from "@/schema/renewal-page/review-page.schema";
import { RootState } from "@/store/root-reducer";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import QuoteDataMap from "@/utils/gtm/quote-data-map";

interface RenewalCurrentPolicyWrapperProps {
  preferencesContentData: RenewalContactPreferencesContentType;
  beforeContinueContent: ReactElement;
  nextYearRenewalContent: ReactElement;
  policyDocumentContent: ReactElement;
  footerContinueCta: string;
}

function ReviewWrapper({
  beforeContinueContent,
  preferencesContentData,
  nextYearRenewalContent,
  policyDocumentContent,
  footerContinueCta,
}: Readonly<RenewalCurrentPolicyWrapperProps>) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { current } = useWizard();
  const [isdesktop, isReady] = useMediaQuery("(min-width: 600px)");

  const [isOpenModal, setIsOpenModal] = React.useState(false);
  const { hideLoading, showLoading } = useLoading();
  const [shouldTriggerGtm, setShouldTriggerGtm] = React.useState(false);
  const { marketingConsent, paymentDetails, optOutAutoRenewal } =
    useRenewalReviewStateContext();

  const resetTrigger = useCallback(() => {
    hideLoading();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, loading, saveRenewalReview } =
    useSaveRenewalReviewSubmit(resetTrigger);

  const handleClickBack = () => setIsOpenModal(true);
  const handleClickContinue = () => {
    setShouldTriggerGtm(true);
    showLoading();
    saveRenewalReview({
      marketingConsent,
      paymentDetails,
      optOutAutoRenewal,
    });
  };

  useEffect(() => {
    if (!loading && data) {
      dispatch(updateQuote(data));
      router.push(current.next ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dispatch, loading, router]);

  const { referenceId, attributes } = useSelector(
    (state: RootState) => state.quote
  );

  const { pageUrl, pageReferrer, pageTitle } =
    useCustomPageTracking(RenewalReview);

  useGtmPageView({
    userId: "",
    quoteId: "",
    pageReferrer,
    pageUrl,
    pageTitle,
  });

  let gtmQuoteViewData = null;
  if (referenceId) {
    gtmQuoteViewData = QuoteDataMap(
      attributes,
      "renewals_begin_checkout",
      "renewal_checkout",
      referenceId,
      "renewal_journey"
    );
  }

  useGtmViewQuoteData({
    quoteData: {
      ...gtmQuoteViewData,
      renewal_transaction_id: null,
      renewal_payment_method: null,
      voucher_code: null,
      currency: "GBP",
      auto_renewal: !optOutAutoRenewal,
    },
    isReady: shouldTriggerGtm,
  });

  if (!referenceId) return null;

  if (!isReady) return null;

  return (
    <div
      data-testid="renewals-review-test-id"
      className="bg-surface-neutral-option-2 "
    >
      <div className="max-w-[1200px] lg:mx-auto md:mx-[48px] mx-[24px] min-h-[calc(100vh-12px)] md:py-spacing-xxl py-spacing-l md:pb-spacing-5xl lg:pb-spacing-xxl">
        <main className=" grid grid-cols-12 lg:gap-spacing-l md:gap-spacing-xl gap-spacing-l md:mb-0 mb-spacing-5xl ">
          <aside className="grid lg:gap-spacing-xl md:gap-spacing-xl gap-spacing-l lg:col-span-7 col-span-12 lg:order-first">
            <section>
              <div className="bg-surface-neutral-option-1 rounded-radius-xxl">
                <div className="flex flex-col h-full ">
                  <div className="text-surface-neutral-option-1 relative">
                    <HelpOverlayButton />
                    <Logo
                      color="primary"
                      heightClass="h-[80px]"
                      size="xl"
                      showondesktop={isdesktop.toString()}
                    />
                  </div>
                  <div
                    className="w-fit pt-spacing-m pb-spacing-l md:pb-spacing-xxl px-spacing-m md:px-spacing-xxl lg:px-spacing-5xl
                   rounded-radius-xxl bg-surface-neutral-option-1"
                  >
                    {beforeContinueContent}
                  </div>
                </div>
              </div>
            </section>
            <section
              className="w-full pt-spacing-m md:pt-spacing-xxl pb-spacing-l md:pb-spacing-xxl px-spacing-m md:px-spacing-xxl lg:px-spacing-5xl
                   rounded-radius-xxl bg-surface-neutral-option-1"
            >
              <RenewalContactPreferences
                content={preferencesContentData}
                isMobile={!isdesktop}
              />
            </section>
            <section
              className="rounded-radius-xxl bg-surface-neutral-option-1 px-spacing-m pt-spacing-m pb-spacing-l 
            md:px-spacing-xxl md:pt-spacing-xxl md:pb-spacing-xxl lg:px-spacing-5xl"
            >
              {nextYearRenewalContent}
            </section>
          </aside>
          <aside className="lg:col-span-5 col-span-12 md:mb-spacing-l lg:mb-0">
            <section className="rounded-radius-xxl bg-surface-neutral-option-1">
              {policyDocumentContent}
            </section>
          </aside>
        </main>
        <footer>
          <FooterRenewal
            footerContinueCta={footerContinueCta}
            onClickBack={handleClickBack}
            onClickContinue={handleClickContinue}
          />
        </footer>
      </div>
      <UnSavedChangesWarningMessage
        isOpen={isOpenModal}
        setIsOpen={setIsOpenModal}
        onContinueClick={() => {
          setIsOpenModal(false);
          router.push(current.prev ?? "");
        }}
      />
    </div>
  );
}

export default withQuoteData(ReviewWrapper);
